/**
 * WellDetail — Phase 24 / Task 24.11 (DEC-104).
 *
 * Read-only platform-catalog well header + this tenant's standalone tanks +
 * per-well audit trail. Managers can add / edit / deactivate standalone tanks
 * via an inline form. When the well is already linked to a tank battery for this
 * tenant, an amber conflict banner replaces the Add Tank affordance (the two
 * models are mutually exclusive per well per hauler — enforced server-side).
 */
import * as React from 'react';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { Combobox } from '../../components/Combobox';
import { fieldStyle, labelStyle, FormRow, ProducerIdHint } from './form-bits';
import {
  TankThresholdFields,
  thresholdPatch,
  thresholdValuesFrom,
  type TankThresholdValues,
} from './TankThresholdFields';
import { rowStyle, subText, Pill, ActiveBadge, AddButton } from './ui-bits';
import { AutoHaulSwitch } from './AutoHaulSwitch';
import { standardTankLabel, suggestBblPerInch } from '@lodex-pro/shared/tank-strapping';
import { TankSizeHint } from './TankSizeHint';
import {
  useWellDetail,
  useWellTanks,
  useSetWellAutoHaul,
  useWellAudit,
  WELL_AUDIT_KEY,
  useAddWellTank,
  useUpdateWellTank,
  useDeactivateWellTank,
  useStandardTanks,
  roleLabel,
  type WellTank,
} from './hooks';
import { groupAudit, type AuditGroupT } from './audit-grouping';
import { RequestorSlots } from '../requestors/RequestorSlots';
import { PlaceNotesEditor } from '../place-notes/PlaceNotesEditor';
import { formatNumber } from '@lodex-pro/shared/number-format';

const sectionTitle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 700,
  color: 'var(--color-text-tertiary)',
  margin: '18px 0 8px',
};

/** Local-time audit timestamp (no UTC shown to users); raw iso on parse failure. */
function fmtAuditTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function toNum(s: string): number | undefined {
  if (s.trim() === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

export function WellDetail({
  wellId,
  canManage,
  onClose,
}: {
  readonly wellId: string;
  readonly canManage: boolean;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { data: wellData, isLoading } = useWellDetail(wellId);
  const { data: tanksData } = useWellTanks(wellId);
  const { data: auditData } = useWellAudit(wellId);
  const well = wellData?.well;
  const tanks = tanksData?.tanks ?? [];
  // 13.4a (0236) — the hauler's auto-haul switch for this well (null: the hauler has no row).
  const setAutoHaul = useSetWellAutoHaul(wellId);
  const wellAutoHaul = tanksData?.autoHaulEnabled;
  const audit = auditData?.audit ?? [];

  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<WellTank | null>(null);

  const inBattery = well?.tankBattery != null;

  return (
    <div>
      <button
        type="button"
        onClick={onClose}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: 'none',
          border: 'none',
          color: 'var(--color-accent-blue)',
          cursor: 'pointer',
          fontSize: '13px',
          marginBottom: '12px',
          padding: 0,
        }}
      >
        <ArrowLeft size={14} aria-hidden="true" /> Back to wells
      </button>

      {isLoading && <p style={subText}>Loading…</p>}
      {well === undefined && !isLoading && (
        <p style={{ ...subText, color: 'var(--color-danger)' }}>Could not load this well.</p>
      )}

      {well !== undefined && (
        <>
          {/* ── Read-only platform-catalog header ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--color-text-primary)' }}>
              {well.wellName}
            </h2>
            <Pill label="lodeX Pro Catalog" tone="accent" />
            {/* "In your catalog" badge intentionally omitted here — on the detail
                page it is self-evident the well is in the catalog (kept on the Wells
                tab search results, where the context is needed). 86-followup #5. */}
            {!well.isActive && <ActiveBadge active={false} />}
          </div>
          <div style={{ ...subText, marginTop: '4px' }}>API {well.apiNumber}</div>
          <div style={{ ...subText, marginTop: '2px' }}>
            {well.operatorName ?? 'Unknown operator'}
            {[well.county, well.state].filter((s) => s != null && s !== '').length > 0
              ? ` · ${[well.county, well.state].filter((s) => s != null && s !== '').join(', ')}`
              : ''}
            {well.wellType != null ? ` · ${well.wellType}` : ''}
          </div>

          {/* ── Requestors (OPEN-053) ── the two people who call in loads for this well.
              Separate from the site's slots: a well that is dispatched directly needs its
              own, and a well inside a tank battery may have a different contact. */}
          <RequestorSlots targetType="WELL" targetId={wellId} canManage={canManage} />

          {/* ── Conflict banner (well already in a tank battery for this tenant) ── */}
          {inBattery && (
            <div
              role="status"
              style={{
                marginTop: '14px',
                padding: '10px 12px',
                borderRadius: '12px',
                fontSize: '13px',
                backgroundColor: 'rgba(246, 182, 94,0.12)',
                border: '1px solid rgba(246, 182, 94,0.4)',
                color: 'var(--color-warning)',
              }}
            >
              This well is part of {well.tankBattery?.name}. Remove it from the battery before
              adding a standalone tank.
            </div>
          )}

          {/* ── Tanks ── */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              ...sectionTitle,
            }}
          >
            <span>Tanks at this well ({tanks.length})</span>
            {canManage && !inBattery && !formOpen && (
              <AddButton
                label="Add Tank"
                onClick={(): void => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              />
            )}
          </div>

          {/* 13.4a — auto-haul for this well's standalone tanks, switched on per site. */}
          {!inBattery && tanks.length > 0 && typeof wellAutoHaul === 'boolean' && (
            <AutoHaulSwitch
              enabled={wellAutoHaul}
              canManage={canManage}
              pending={setAutoHaul.isPending}
              idSuffix={wellId}
              onChange={(next, done): void => setAutoHaul.mutate({ enabled: next }, done)}
            />
          )}

          {tanks.length === 0 && !formOpen && (
            <p style={subText}>No tanks configured yet. Add a tank to start tracking volumes.</p>
          )}

          {tanks.map((t) => (
            <TankRow
              key={t.id}
              tank={t}
              canManage={canManage}
              onEdit={(): void => {
                setEditing(t);
                setFormOpen(true);
              }}
            />
          ))}

          {formOpen && (
            <AddWellTankForm
              wellId={wellId}
              editing={editing}
              onClose={(): void => {
                setFormOpen(false);
                setEditing(null);
              }}
            />
          )}

          {/* ── Notes for drivers (#45 / DEC-359) — recorded in the audit trail below ── */}
          <div style={sectionTitle}>Notes for drivers</div>
          <PlaceNotesEditor
            kind="WELL"
            id={wellId}
            placeName={well.wellName}
            canManage={canManage && well.isActive}
            alsoInvalidate={[WELL_AUDIT_KEY(wellId)]}
          />

          {/* ── Audit trail (grouped by actor + 5s window, 86-followup #4) ── */}
          <div style={sectionTitle}>Audit trail</div>
          {audit.length === 0 && <p style={subText}>No changes recorded.</p>}
          {groupAudit(audit).map((g) => (
            <AuditGroup key={g.items[0]?.id ?? ''} group={g} />
          ))}
        </>
      )}
    </div>
  );
}

function TankRow({
  tank,
  canManage,
  onEdit,
}: {
  tank: WellTank;
  canManage: boolean;
  onEdit: () => void;
}): React.JSX.Element {
  const deactivate = useDeactivateWellTank(tank.id);
  const { data: std } = useStandardTanks();
  const specs = [
    tank.capacityBbl != null ? `${formatNumber(tank.capacityBbl)} bbl` : null,
    tank.bblPerInch != null ? `${tank.bblPerInch} bbl/in` : null,
    tank.tankType,
    tank.producerIdentifier,
  ]
    .filter((s) => s != null && s !== '')
    .join(' · ');
  return (
    <div style={{ ...rowStyle, opacity: tank.isActive ? 1 : 0.55 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{tank.name}</div>
        {specs !== '' && <div style={subText}>{specs}</div>}
        {/* DEC-377 — no bbl per inch: the standard size its estimate uses, or the options. */}
        {tank.isActive && (
          <TankSizeHint
            tankId={tank.id}
            bblPerInch={tank.bblPerInch}
            suggestion={suggestBblPerInch(tank, std?.standardTanks ?? [])}
            canManage={canManage}
            whereToSet={canManage ? 'with Edit' : 'on this tank (a Dispatch Manager can)'}
            refresh={[['wells']]}
          />
        )}
      </div>
      <ActiveBadge active={tank.isActive} />
      {canManage && tank.isActive && (
        <>
          <button
            type="button"
            onClick={onEdit}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-accent-blue)',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Edit
          </button>
          <button
            type="button"
            disabled={deactivate.isPending}
            onClick={(): void =>
              deactivate.mutate(undefined, {
                onSuccess: () => toast.success('Tank deactivated'),
                onError: () => toast.error('Could not deactivate the tank.'),
              })
            }
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-danger)',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Deactivate
          </button>
        </>
      )}
    </div>
  );
}

function AuditGroup({ group }: { group: AuditGroupT }): React.JSX.Element | null {
  const head = group.items[0];
  if (head === undefined) return null;
  const actor = `${head.changedByName ?? 'System'}${
    head.changedByRole != null ? ` (${roleLabel(head.changedByRole)})` : ''
  }`;

  // Single event — unchanged from the original format.
  if (group.items.length === 1) {
    return (
      <div style={{ ...rowStyle, display: 'block' }}>
        <div style={subText}>{fmtAuditTime(head.changedAt)}</div>
        <div style={{ fontSize: '13px' }}>{actor}</div>
        <div style={{ fontSize: '14px' }}>{head.notes ?? head.action}</div>
      </div>
    );
  }

  // Grouped — one header (timestamp — actor), then a bullet per event.
  return (
    <div style={{ ...rowStyle, display: 'block' }}>
      <div style={subText}>
        {fmtAuditTime(head.changedAt)} — {actor}
      </div>
      <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
        {group.items.map((e) => (
          <li key={e.id} style={{ fontSize: '14px' }}>
            {e.notes ?? e.action}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Inline add/edit form for a standalone well tank (mirrors AddTankModal). */
function AddWellTankForm({
  wellId,
  editing,
  onClose,
}: {
  wellId: string;
  editing: WellTank | null;
  onClose: () => void;
}): React.JSX.Element {
  const isEdit = editing != null;
  const { data: stdData } = useStandardTanks();
  const standardTanks = (stdData?.standardTanks ?? []).filter((s) => s.isActive);

  const [standardId, setStandardId] = React.useState('');
  const [name, setName] = React.useState(editing?.name ?? '');
  const [capacity, setCapacity] = React.useState(editing?.capacityBbl ?? '');
  const [bblPerInch, setBblPerInch] = React.useState(editing?.bblPerInch ?? '');
  const [tankType, setTankType] = React.useState(editing?.tankType ?? '');
  const [producerId, setProducerId] = React.useState(editing?.producerIdentifier ?? '');
  const [error, setError] = React.useState<string | null>(null);
  // Phase 13.4a (0236) — the haul threshold, edited with the tank.
  const [threshold, setThreshold] = React.useState<TankThresholdValues>(
    thresholdValuesFrom(editing ?? null),
  );

  const addMutation = useAddWellTank(wellId, () => {
    toast.success('Tank added');
    onClose();
  });
  const updateMutation = useUpdateWellTank(editing?.id ?? '');
  const pending = addMutation.isPending || updateMutation.isPending;

  const onPickStandard = (id: string): void => {
    setStandardId(id);
    const std = standardTanks.find((s) => s.id === id);
    if (std) {
      setCapacity(std.capacityBbl);
      setBblPerInch(std.bblPerInch);
      if (name.trim() === '') setName(std.name);
    }
  };

  const handleSave = (): void => {
    if (name.trim() === '') {
      setError('Tank name is required.');
      return;
    }
    setError(null);
    const onError = (): void => setError('Could not save the tank. Try again.');
    if (isEdit) {
      const th = thresholdPatch(threshold);
      if (!th.ok) {
        setError(th.error);
        return;
      }
      updateMutation.mutate(
        {
          ...th.body,
          name: name.trim(),
          capacityBbl: toNum(capacity) ?? null,
          bblPerInch: toNum(bblPerInch) ?? null,
          tankType: tankType.trim() === '' ? null : tankType.trim(),
          producerIdentifier: producerId.trim() === '' ? null : producerId.trim(),
        },
        {
          onSuccess: () => {
            toast.success('Tank updated');
            onClose();
          },
          onError,
        },
      );
      return;
    }
    const cap = toNum(capacity);
    const bpi = toNum(bblPerInch);
    addMutation.mutate(
      {
        name: name.trim(),
        ...(cap !== undefined && { capacityBbl: cap }),
        ...(bpi !== undefined && { bblPerInch: bpi }),
        ...(tankType.trim() !== '' && { tankType: tankType.trim() }),
        ...(producerId.trim() !== '' && { producerIdentifier: producerId.trim() }),
        ...(standardId !== '' && { standardTankCatalogId: standardId }),
      },
      { onError },
    );
  };

  return (
    <div
      style={{
        marginTop: '8px',
        padding: '12px',
        borderRadius: '6px',
        border: '1px solid var(--color-border-secondary)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--color-text-primary)' }}>
        {isEdit ? 'Edit tank' : 'Add tank'}
      </div>
      {!isEdit && (
        <FormRow label="Select from standard catalog (optional)" htmlFor="well-tank-std">
          <Combobox
            id="well-tank-std"
            options={standardTanks.map((s) => ({ value: s.id, label: standardTankLabel(s) }))}
            value={standardId}
            onChange={onPickStandard}
            placeholder="Pick from catalog…"
            searchPlaceholder="Search standard tanks…"
            emptyText="No standard tanks."
          />
        </FormRow>
      )}
      <FormRow label="Name" htmlFor="well-tank-name" required>
        <input
          id="well-tank-name"
          value={name}
          onChange={(e): void => setName(e.target.value)}
          placeholder="e.g. Tank 1"
          style={fieldStyle}
        />
      </FormRow>
      <div style={{ display: 'flex', gap: '12px' }}>
        <FormRow label="Capacity (bbl)" htmlFor="well-tank-cap">
          <input
            id="well-tank-cap"
            inputMode="decimal"
            value={capacity}
            onChange={(e): void => setCapacity(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow label="BBL per inch" htmlFor="well-tank-bpi">
          <input
            id="well-tank-bpi"
            inputMode="decimal"
            value={bblPerInch}
            onChange={(e): void => setBblPerInch(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
      </div>
      <FormRow label="Tank type" htmlFor="well-tank-type">
        <input
          id="well-tank-type"
          value={tankType}
          onChange={(e): void => setTankType(e.target.value)}
          placeholder="e.g. GUN BARREL or WATER"
          style={fieldStyle}
        />
      </FormRow>
      <FormRow label="Producer identifier" htmlFor="well-tank-pid">
        <input
          id="well-tank-pid"
          value={producerId}
          onChange={(e): void => setProducerId(e.target.value)}
          placeholder="What the producer calls this tank"
          aria-describedby={producerId.trim() === '' ? 'well-tank-pid-hint' : undefined}
          style={fieldStyle}
        />
        <ProducerIdHint id="well-tank-pid-hint" value={producerId} />
      </FormRow>
      {standardId !== '' &&
        (() => {
          const std = standardTanks.find((s) => s.id === standardId);
          return std ? (
            <p style={{ ...labelStyle, textTransform: 'none' }}>Based on: {std.name}</p>
          ) : null;
        })()}
      {isEdit && (
        <TankThresholdFields value={threshold} onChange={setThreshold} idPrefix="well-tank-th" />
      )}
      {error !== null && (
        <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={pending}
          onClick={handleSave}
          style={{
            backgroundColor: 'var(--color-accent-blue)',
            color: 'var(--color-on-accent)',
            borderColor: 'var(--color-accent-blue)',
          }}
        >
          {pending ? 'Saving…' : 'Save Tank'}
        </Button>
      </div>
    </div>
  );
}
